import { useState, useRef, useCallback, useEffect } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a4b0982a-6afd-4aee-bd1d-cfdc19137757/artifacts/4d9u8dkr_WhatsApp%20Image%202026-03-18%20at%2011.28.54.jpeg";

// Frequency test configuration
const FREQS = [500, 1000, 2000, 3000, 4000, 8000];
const FREQ_LABELS = ['500 Hz', '1 kHz', '2 kHz', '3 kHz', '4 kHz', '8 kHz'];

function App() {
  const [currentScreen, setCurrentScreen] = useState('hero');
  const [answers, setAnswers] = useState(Array(6).fill(null));
  const [leftValues, setLeftValues] = useState([50, 50, 50, 50, 50, 50]);
  const [rightValues, setRightValues] = useState([50, 50, 50, 50, 50, 50]);
  const [calibrated, setCalibrated] = useState(false);
  const [calPlaying, setCalPlaying] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '' });
  const [leadId, setLeadId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const audioCtxRef = useRef(null);
  const calOscillatorRef = useRef(null);
  const calGainRef = useRef(null);
  const canvasRef = useRef(null);
  const barAnimRef = useRef(null);

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  }, []);

  const playTone = useCallback((freq, vol, channel, duration) => {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') ctx.resume();
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    
    const amp = Math.pow(10, (vol - 100) / 20) * 0.3;
    gain.gain.setValueAtTime(amp, ctx.currentTime);
    
    if (ctx.createStereoPanner) {
      const pan = ctx.createStereoPanner();
      pan.pan.value = channel === 'left' ? -1 : channel === 'right' ? 1 : 0;
      osc.connect(gain);
      gain.connect(pan);
      pan.connect(ctx.destination);
    } else {
      osc.connect(gain);
      gain.connect(ctx.destination);
    }
    
    osc.start();
    if (duration) {
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + duration - 0.02);
      osc.stop(ctx.currentTime + duration);
    }
    
    return { osc, gain };
  }, [getAudioCtx]);

  const showScreen = (screen) => {
    setCurrentScreen(screen);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (calPlaying && screen !== 'cal2') {
      toggleCalibration();
    }
  };

  const selectAnswer = (q, val) => {
    const newAnswers = [...answers];
    newAnswers[q] = val;
    setAnswers(newAnswers);
  };

  const goAfterQuestionnaire = () => {
    showScreen(answers.some(a => a === 'yes') ? 'redflag' : 'quiet');
  };

  const toggleCalibration = () => {
    if (!calPlaying) {
      const t = playTone(1000, 65, 'both', null);
      calOscillatorRef.current = t.osc;
      calGainRef.current = t.gain;
      setCalPlaying(true);
      
      barAnimRef.current = setInterval(() => {
        const bars = document.querySelectorAll('.cal-bar');
        bars.forEach(b => {
          b.style.height = (20 + Math.random() * 80) + '%';
          b.classList.add('active');
        });
      }, 80);
    } else {
      try {
        calOscillatorRef.current?.stop();
        calGainRef.current?.disconnect();
      } catch(e) {}
      setCalPlaying(false);
      clearInterval(barAnimRef.current);
      
      const heights = [30, 50, 70, 90, 100, 90, 70, 50, 30];
      document.querySelectorAll('.cal-bar').forEach((b, i) => {
        b.style.height = heights[i] + '%';
        b.classList.remove('active');
      });
    }
  };

  const handleSliderChange = (ear, index, value) => {
    const val = parseInt(value);
    if (ear === 'left') {
      const newValues = [...leftValues];
      newValues[index] = val;
      setLeftValues(newValues);
    } else {
      const newValues = [...rightValues];
      newValues[index] = val;
      setRightValues(newValues);
    }
    playTone(FREQS[index], val, ear, 0.6);
  };

  const adjustSlider = (ear, index, delta) => {
    const values = ear === 'left' ? leftValues : rightValues;
    const setValues = ear === 'left' ? setLeftValues : setRightValues;
    const newVal = Math.max(0, Math.min(100, values[index] + delta));
    const newValues = [...values];
    newValues[index] = newVal;
    setValues(newValues);
    playTone(FREQS[index], newVal, ear, 0.6);
  };

  const handleLeadSubmit = async () => {
    if (!leadData.name || !leadData.email) {
      alert('Please enter your name and email to continue.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/leads`, leadData);
      setLeadId(response.data.id);
      showScreen('questionnaire');
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('There was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitTestResults = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/test-results`, {
        lead_id: leadId,
        left_values: leftValues,
        right_values: rightValues,
        questionnaire_answers: answers
      });
      setTestResult(response.data);
      showScreen('results');
    } catch (error) {
      console.error('Error submitting results:', error);
      alert('There was an error saving your results. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const classify = (vals) => {
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    if (avg < 30) return 'normal';
    if (avg < 50) return 'mild';
    if (avg < 70) return 'moderate';
    return 'severe';
  };

  // Draw audiogram on results screen
  useEffect(() => {
    if (currentScreen === 'results' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const W = canvas.width, H = canvas.height;
      const pad = { top: 32, right: 40, bottom: 64, left: 64 };
      const pW = W - pad.left - pad.right;
      const pH = H - pad.top - pad.bottom;
      
      ctx.clearRect(0, 0, W, H);
      
      // Zones
      [
        ['Normal', 0, 25, 'rgba(34,197,94,.12)'],
        ['Mild', 25, 40, 'rgba(234,179,8,.12)'],
        ['Moderate', 40, 70, 'rgba(249,115,22,.12)'],
        ['Severe', 70, 100, 'rgba(239,68,68,.12)']
      ].forEach(([label, from, to, color]) => {
        const y1 = pad.top + (from / 100) * pH;
        const y2 = pad.top + (to / 100) * pH;
        ctx.fillStyle = color;
        ctx.fillRect(pad.left, y1, pW, y2 - y1);
        ctx.fillStyle = 'rgba(30,138,124,.6)';
        ctx.font = '10px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(label, pad.left + 6, y1 + 13);
      });
      
      // Grid
      ctx.strokeStyle = 'rgba(30,138,124,.15)';
      ctx.lineWidth = 1;
      for (let db = 0; db <= 100; db += 25) {
        const y = pad.top + (db / 100) * pH;
        ctx.beginPath();
        ctx.moveTo(pad.left, y);
        ctx.lineTo(pad.left + pW, y);
        ctx.stroke();
        ctx.fillStyle = 'rgba(30,138,124,.7)';
        ctx.font = '11px system-ui, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(db + ' dB', pad.left - 8, y + 4);
      }
      
      for (let i = 0; i < 6; i++) {
        const x = pad.left + (i / 5) * pW;
        ctx.beginPath();
        ctx.moveTo(x, pad.top);
        ctx.lineTo(x, pad.top + pH);
        ctx.stroke();
        ctx.fillStyle = 'rgba(30,138,124,.7)';
        ctx.font = '11px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(FREQ_LABELS[i], x, pad.top + pH + 20);
      }
      
      // Axis labels
      ctx.fillStyle = 'rgba(30,138,124,.8)';
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Frequency', pad.left + pW / 2, H - 6);
      ctx.save();
      ctx.translate(14, pad.top + pH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('Hearing Level (dB)', 0, 0);
      ctx.restore();
      
      // Ear lines
      [
        { vals: leftValues, color: '#3b82f6', sym: 'X', label: 'Left Ear' },
        { vals: rightValues, color: '#f43f5e', sym: 'O', label: 'Right Ear' }
      ].forEach(({ vals, color, sym }) => {
        const pts = vals.map((v, i) => ({
          x: pad.left + (i / 5) * pW,
          y: pad.top + (v / 100) * pH
        }));
        
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.stroke();
        
        pts.forEach(p => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
          ctx.fillStyle = 'white';
          ctx.fill();
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.fillStyle = color;
          ctx.font = 'bold 9px system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(sym, p.x, p.y + 3.5);
        });
      });
      
      // Legend box
      const lx = pad.left + pW - 148, ly = pad.top + 10;
      ctx.fillStyle = 'rgba(255,255,255,.95)';
      ctx.beginPath();
      ctx.roundRect(lx - 8, ly - 8, 156, 52, 8);
      ctx.fill();
      ctx.strokeStyle = 'rgba(30,138,124,.2)';
      ctx.stroke();
      
      [['#3b82f6', 'X', 'Left Ear'], ['#f43f5e', 'O', 'Right Ear']].forEach(([c, s, l], i) => {
        ctx.fillStyle = c;
        ctx.font = 'bold 11px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(s + ' — ' + l, lx, ly + 12 + i * 20);
      });
    }
  }, [currentScreen, leftValues, rightValues]);

  const downloadResults = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      alert('Please wait for the results to load completely.');
      return;
    }
    
    try {
      // Create a temporary canvas with white background and title for better PNG export
      const tempCanvas = document.createElement('canvas');
      const titleHeight = 60;
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height + titleHeight;
      const tempCtx = tempCanvas.getContext('2d');
      
      // Fill white background
      tempCtx.fillStyle = '#ffffff';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      
      // Add title
      tempCtx.fillStyle = '#1a2332';
      tempCtx.font = 'bold 18px Georgia, serif';
      tempCtx.textAlign = 'center';
      tempCtx.fillText('Suncoast Hearing Center - Hearing Test Results', tempCanvas.width / 2, 35);
      
      // Draw the original canvas content below title
      tempCtx.drawImage(canvas, 0, titleHeight);
      
      // Convert to data URL and download
      const dataUrl = tempCanvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'suncoast-hearing-test-results.png';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  const retakeTest = () => {
    setAnswers(Array(6).fill(null));
    setLeftValues([50, 50, 50, 50, 50, 50]);
    setRightValues([50, 50, 50, 50, 50, 50]);
    setCalibrated(false);
    setCalPlaying(false);
    setLeadData({ name: '', email: '', phone: '' });
    setLeadId(null);
    setTestResult(null);
    showScreen('hero');
  };

  const getResultInfo = () => {
    const overall = testResult?.overall_classification || classify([...leftValues, ...rightValues].map((_, i) => i < 6 ? leftValues[i] : rightValues[i - 6]));
    
    const leftClass = classify(leftValues);
    const rightClass = classify(rightValues);
    const rank = ['normal', 'mild', 'moderate', 'severe'];
    const finalClass = rank[Math.max(rank.indexOf(leftClass), rank.indexOf(rightClass))];
    
    const titles = {
      normal: 'Your hearing looks healthy!',
      mild: 'You have mild hearing loss at some frequencies.',
      moderate: 'You have moderate hearing loss at some frequencies.',
      severe: 'You have significant hearing loss.'
    };
    
    const badges = {
      normal: ['normal', 'Normal Hearing'],
      mild: ['mild', 'Mild Hearing Loss'],
      moderate: ['moderate', 'Moderate Hearing Loss'],
      severe: ['severe', 'Severe Hearing Loss']
    };
    
    const descs = {
      normal: 'Great news! Your hearing thresholds appear within normal range across most frequencies. Continue protecting your hearing with ear protection in loud environments.',
      mild: 'You may notice difficulty with soft sounds or speech in noisy environments. Mild hearing loss is common and very treatable. A consultation can determine the best path forward.',
      moderate: 'Moderate hearing loss means you likely struggle to follow conversations without hearing aids. Prescription aids from leading brands can dramatically improve your quality of life.',
      severe: 'Severe hearing loss significantly impacts daily communication. We strongly recommend a full clinical audiogram and a prescription hearing aid fitting from a licensed audiologist.'
    };
    
    return {
      classification: finalClass,
      title: titles[finalClass],
      badge: badges[finalClass],
      description: descs[finalClass]
    };
  };

  const renderSliders = (ear, values, setValues) => (
    <div className="slider-grid">
      {FREQS.map((freq, i) => (
        <div key={i} className="freq-row">
          <div className="freq-label">{FREQ_LABELS[i]}</div>
          <input
            type="range"
            className="custom-slider"
            min="0"
            max="100"
            value={values[i]}
            onChange={(e) => handleSliderChange(ear, i, e.target.value)}
            style={{ '--val': `${values[i]}%` }}
            data-testid={`slider-${ear}-${i}`}
          />
          <button 
            className="adj-btn" 
            onClick={() => adjustSlider(ear, i, -5)}
            data-testid={`btn-minus-${ear}-${i}`}
          >-</button>
          <button 
            className="adj-btn" 
            onClick={() => adjustSlider(ear, i, 5)}
            data-testid={`btn-plus-${ear}-${i}`}
          >+</button>
        </div>
      ))}
      <div className="slider-hint">
        <span>Quieter</span>
        <span>Louder</span>
      </div>
    </div>
  );

  const FreqLegend = () => {
    const bands = [
      { label: 'Low Freq (500-1kHz)', val: Math.round((leftValues[0] + leftValues[1] + rightValues[0] + rightValues[1]) / 4) },
      { label: 'Mid Freq (2-3kHz)', val: Math.round((leftValues[2] + leftValues[3] + rightValues[2] + rightValues[3]) / 4) },
      { label: 'High Freq (4-8kHz)', val: Math.round((leftValues[4] + leftValues[5] + rightValues[4] + rightValues[5]) / 4) }
    ];
    
    return (
      <div className="freq-legend">
        {bands.map((band, i) => {
          const cls = band.val < 30 ? 'good' : band.val < 60 ? 'ok' : 'bad';
          const label = band.val < 30 ? 'Good' : band.val < 60 ? 'Mild Loss' : 'Loss';
          return (
            <div key={i} className="legend-item">
              <div className="legend-freq">{band.label}</div>
              <div className={`legend-value ${cls}`}>{label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="app-container">
      <div className="bg-mesh"></div>
      <div className="container">
        <header className="site-header">
          <img src={LOGO_URL} alt="Suncoast Hearing Center" className="logo-img" />
        </header>

        {/* HERO SCREEN */}
        {currentScreen === 'hero' && (
          <div className="hero" data-testid="screen-hero">
            <div className="hero-badge">Free Online Hearing Test</div>
            <h1>How well do you<br /><em>really</em> hear?</h1>
            <p className="hero-sub">
              Our audiologist-designed screener tests your hearing across 6 key frequencies 
              and gives you a clinical-style audiogram in minutes.
            </p>
            <button 
              className="cta-btn" 
              onClick={() => showScreen('lead')}
              data-testid="start-test-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none"/>
              </svg>
              Start Free Test
            </button>
            <div className="trust-row">
              <span className="trust-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                5 Minutes
              </span>
              <span className="trust-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Privacy Protected
              </span>
              <span className="trust-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
                Instant Results
              </span>
            </div>
          </div>
        )}

        {/* LEAD CAPTURE SCREEN (FIRST) */}
        {currentScreen === 'lead' && (
          <div data-testid="screen-lead">
            <div className="step-counter">Let's Get Started</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '8%' }}></div>
            </div>
            <div className="card">
              <div className="card-title">Enter your details to <em>begin</em></div>
              <div className="card-sub">
                We'll email your results and personalized recommendations. Your information is kept private.
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    placeholder="Jane Smith"
                    value={leadData.name}
                    onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    data-testid="input-name"
                  />
                </div>
                <div className="form-field">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    placeholder="jane@email.com"
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    data-testid="input-email"
                  />
                </div>
                <div className="form-field">
                  <label>Phone Number (Optional)</label>
                  <input
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={leadData.phone}
                    onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                    data-testid="input-phone"
                  />
                </div>
              </div>
              <p className="privacy-note">
                By continuing you agree to our Privacy Policy. We'll use your info to send your results 
                and recommend hearing solutions. No spam, ever.
              </p>
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('hero')} data-testid="back-btn">
                  Back
                </button>
                <button 
                  className="next-btn" 
                  onClick={handleLeadSubmit}
                  disabled={isSubmitting}
                  data-testid="continue-btn"
                >
                  {isSubmitting ? 'Saving...' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QUESTIONNAIRE SCREEN */}
        {currentScreen === 'questionnaire' && (
          <div data-testid="screen-questionnaire">
            <div className="step-counter">Step 1 of 6 - Symptom Check</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '16%' }}></div>
            </div>
            <div className="card">
              <div className="card-title">A few quick <em>questions</em> before we begin</div>
              <div className="card-sub">These help us flag symptoms that need professional attention.</div>
              
              {[
                'Have you recently had a sudden change in hearing?',
                'Are you experiencing pain in your ears?',
                'Are you experiencing dizziness or vertigo?',
                'Have your ears leaked fluid in the last 90 days?',
                'Do you have visible earwax buildup?',
                'Have you suffered a recent head injury?'
              ].map((question, i) => (
                <div key={i} className="question-block">
                  <div className="question-label">{i + 1}. {question}</div>
                  <div className="btn-group">
                    <button
                      className={`choice-btn ${answers[i] === 'yes' ? 'danger-selected' : ''}`}
                      onClick={() => selectAnswer(i, 'yes')}
                      data-testid={`q${i}-yes`}
                    >
                      Yes
                    </button>
                    <button
                      className={`choice-btn ${answers[i] === 'no' ? 'selected' : ''}`}
                      onClick={() => selectAnswer(i, 'no')}
                      data-testid={`q${i}-no`}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}
              
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('lead')} data-testid="back-btn">
                  Back
                </button>
                <button
                  className="next-btn"
                  onClick={goAfterQuestionnaire}
                  disabled={!answers.every(a => a !== null)}
                  data-testid="continue-btn"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RED FLAG SCREEN */}
        {currentScreen === 'redflag' && (
          <div data-testid="screen-redflag">
            <div className="step-counter">Recommendation</div>
            <div className="card">
              <div className="alert-box">
                <h3>We recommend seeing a specialist first</h3>
                <p>
                  Based on your answers, you may have symptoms that need professional evaluation 
                  before a self-screening test. An ENT doctor or audiologist can give you a thorough, 
                  accurate diagnosis.
                </p>
              </div>
              <p className="disclaimer-text">
                You can still proceed for informational purposes, but do not use these results 
                as a substitute for medical advice.
              </p>
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('questionnaire')} data-testid="back-btn">
                  Back
                </button>
                <button className="next-btn" onClick={() => showScreen('quiet')} data-testid="proceed-btn">
                  Proceed Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QUIET ROOM SCREEN */}
        {currentScreen === 'quiet' && (
          <div data-testid="screen-quiet">
            <div className="step-counter">Step 2 of 6 - Environment</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '33%' }}></div>
            </div>
            <div className="card">
              <div className="card-title">Are you in a <em>quiet room?</em></div>
              <div className="card-sub">
                Background noise — traffic, conversations, appliances — will significantly 
                affect your results. Find the quietest spot you can before continuing.
              </div>
              <div className="emoji-display">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 9a3 3 0 1 0 6 0" />
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 18v-2" />
                  <path d="M8 15h.01" />
                  <path d="M16 15h.01" />
                </svg>
              </div>
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('questionnaire')} data-testid="back-btn">
                  Back
                </button>
                <button className="next-btn" onClick={() => showScreen('headphones')} data-testid="quiet-confirm-btn">
                  I'm in a quiet room
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HEADPHONES SCREEN */}
        {currentScreen === 'headphones' && (
          <div data-testid="screen-headphones">
            <div className="step-counter">Step 3 of 6 - Equipment</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '50%' }}></div>
            </div>
            <div className="card">
              <div className="card-title">You'll need <em>headphones</em></div>
              <div className="card-sub">
                Headphones let us test each ear separately. Earbuds or over-ear — any quality 
                pair works. Sony, Bose, AirPods, Samsung, and Jabra all work great.
              </div>
              <div className="emoji-display">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" />
                  <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" />
                </svg>
              </div>
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('quiet')} data-testid="back-btn">
                  Back
                </button>
                <button className="next-btn" onClick={() => showScreen('cal1')} data-testid="headphones-confirm-btn">
                  I'm wearing headphones
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CALIBRATION 1 SCREEN */}
        {currentScreen === 'cal1' && (
          <div data-testid="screen-cal1">
            <div className="step-counter">Step 4 of 6 - Volume Calibration (1/2)</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '66%' }}></div>
            </div>
            <div className="card">
              <div className="card-title">Let's set the right <em>volume</em></div>
              <div className="card-sub">
                First, take your headphones OFF and rub your hands together quickly and firmly, 
                close to your nose.
              </div>
              <div className="emoji-display">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M7 11.5V14a5 5 0 0 0 10 0v-2.5" />
                  <path d="M11 4a2 2 0 0 1 4 0v6" />
                  <path d="M7 9V4a2 2 0 0 1 4 0v6" />
                  <path d="M15 9V4a2 2 0 0 1 4 0v6" />
                  <path d="M3 9v2a9 9 0 0 0 18 0V9" />
                </svg>
              </div>
              <p className="hint-text">
                Hand rubbing produces a consistent ~65dB reference sound we use to calibrate 
                your device volume.
              </p>
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('headphones')} data-testid="back-btn">
                  Back
                </button>
                <button className="next-btn" onClick={() => showScreen('cal2')} data-testid="hands-rubbed-btn">
                  Done, hands rubbed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CALIBRATION 2 SCREEN */}
        {currentScreen === 'cal2' && (
          <div data-testid="screen-cal2">
            <div className="step-counter">Step 4 of 6 - Volume Calibration (2/2)</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '75%' }}></div>
            </div>
            <div className="card">
              <div className="card-title">Match the <em>volume</em></div>
              <div className="card-sub">
                Put your headphones back on. Play the calibration tone and adjust your device 
                volume until it matches the sound your hands made.
              </div>
              <div className="cal-visual">
                {[30, 50, 70, 90, 100, 90, 70, 50, 30].map((h, i) => (
                  <div key={i} className="cal-bar" style={{ height: `${h}%` }}></div>
                ))}
              </div>
              <div className="cal-controls">
                <button className="play-btn" onClick={toggleCalibration} data-testid="play-tone-btn">
                  {calPlaying ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16"/>
                      <rect x="14" y="4" width="4" height="16"/>
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5,3 19,12 5,21"/>
                    </svg>
                  )}
                </button>
                <p className="play-hint">Click to {calPlaying ? 'stop' : 'play'} calibration tone</p>
                <button
                  className={`calibrated-check ${calibrated ? 'checked' : ''}`}
                  onClick={() => setCalibrated(!calibrated)}
                  data-testid="calibrated-check"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Volume is calibrated
                </button>
              </div>
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('cal1')} data-testid="back-btn">
                  Back
                </button>
                <button
                  className="next-btn"
                  onClick={() => showScreen('left')}
                  disabled={!calibrated}
                  data-testid="start-test-btn"
                >
                  Start Hearing Test
                </button>
              </div>
            </div>
          </div>
        )}

        {/* LEFT EAR TEST SCREEN */}
        {currentScreen === 'left' && (
          <div data-testid="screen-left">
            <div className="step-counter">Step 5 of 6 - Left Ear Test</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '83%' }}></div>
            </div>
            <div className="card">
              <div className="ear-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0"/>
                  <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 0 0 4 0"/>
                </svg>
                Testing your LEFT ear
              </div>
              <div className="card-title">Drag each slider to the <em>quietest sound</em> you can hear</div>
              <div className="card-sub">
                Cover or ignore your right ear. Move the slider toward "Louder" until the tone 
                is just barely audible.
              </div>
              {renderSliders('left', leftValues, setLeftValues)}
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('cal2')} data-testid="back-btn">
                  Back
                </button>
                <button className="next-btn" onClick={() => showScreen('right')} data-testid="next-right-ear-btn">
                  Next: Right Ear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT EAR TEST SCREEN */}
        {currentScreen === 'right' && (
          <div data-testid="screen-right">
            <div className="step-counter">Step 6 of 6 - Right Ear Test</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '92%' }}></div>
            </div>
            <div className="card">
              <div className="ear-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0"/>
                  <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 0 0 4 0"/>
                </svg>
                Testing your RIGHT ear
              </div>
              <div className="card-title">Now test your <em>right ear</em></div>
              <div className="card-sub">
                Cover or ignore your left ear. Move the slider until the tone is just barely audible.
              </div>
              {renderSliders('right', rightValues, setRightValues)}
              <div className="nav-row">
                <button className="back-btn" onClick={() => showScreen('left')} data-testid="back-btn">
                  Back
                </button>
                <button 
                  className="next-btn" 
                  onClick={submitTestResults}
                  disabled={isSubmitting}
                  data-testid="get-results-btn"
                >
                  {isSubmitting ? 'Saving...' : 'Get My Results'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RESULTS SCREEN */}
        {currentScreen === 'results' && (
          <div data-testid="screen-results">
            <div className="step-counter">Your Results</div>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: '100%' }}></div>
            </div>
            <div className="card">
              <div className="card-title">{getResultInfo().title}</div>
              <div className={`result-badge ${getResultInfo().classification}`}>
                {getResultInfo().badge[1]}
              </div>
              <div className="audiogram-wrap">
                <canvas ref={canvasRef} width="700" height="380" data-testid="audiogram-canvas"></canvas>
              </div>
              <FreqLegend />
              <div className="result-explanation">
                <p className="explanation-title">How to read your results</p>
                <p className="explanation-text">{getResultInfo().description}</p>
              </div>
              <div className="result-cta-box">
                <h3>Ready to Take the Next Step?</h3>
                <p>
                  Our licensed audiologists at Suncoast Hearing Center can provide a comprehensive 
                  evaluation and personalized recommendations.
                </p>
                <a 
                  href="https://suncoasthearingcenters.com/contact" 
                  className="cta-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-consultation-btn"
                >
                  Book a Free Consultation
                </a>
              </div>
              <div className="nav-row result-actions">
                <button className="download-btn" onClick={downloadResults} data-testid="download-results-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                  </svg>
                  Download Results
                </button>
                <button className="retake-btn" onClick={retakeTest} data-testid="retake-test-btn">
                  Retake Test
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
